import { Suspense, lazy, useMemo, useState } from 'react'
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  addDays,
  format,
  parseISO,
  subDays,
} from 'date-fns'
import { pl } from 'date-fns/locale'
import {
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Loader2,
  MapPin,
  MapPinOff,
  Palmtree,
  Plus,
  X,
} from 'lucide-react'
import { formatDistancePl } from '@czas/shared'

import Alert from '@/components/ui/Alert'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import Label from '@/components/ui/Label'
import { apiErrorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import {
  type AdminSessionUser,
  type AdminWorkSession,
  useAdminSessions,
} from '@/features/admin/use-admin-sessions'
import {
  useDeleteSession,
  useUpdateSession,
} from '@/features/admin/use-admin-session-mutations'
import {
  fromLocalInputFormat,
  toLocalInputFormat,
} from '@/features/admin/datetime-local'
import AddSessionModal from '@/features/admin/add-session-modal'
import AuditTrail from '@/features/admin/audit-trail'
import { useAdminVacationRequests, type AdminVacationRequest } from '@/features/admin/use-admin-vacation'
import type { VacationType } from '@/features/vacation/use-vacation'

const VACATION_TYPE_LABEL: Record<VacationType, string> = {
  WYPOCZYNKOWY: 'Wypoczynkowy',
  NA_ZADANIE: 'Na żądanie',
  BEZPLATNY: 'Bezpłatny',
  CHOROBOWY: 'Chorobowy (L4)',
  OPIEKA_NAD_DZIECKIEM: 'Opieka nad dzieckiem',
}

const SessionMap = lazy(() => import('@/features/admin/session-map'))

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

function formatDuration(seconds: number): string {
  if (seconds <= 0) return '0min'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h === 0) return `${m}min`
  if (m === 0) return `${h}h`
  return `${h}h ${m}min`
}

function sessionDurationSec(s: AdminWorkSession): number | null {
  if (!s.endedAt) return null
  return (parseISO(s.endedAt).getTime() - parseISO(s.startedAt).getTime()) / 1000
}

/**
 * M8 — pracownik kliknął „Koniec pracy" daleko od placówki i nie odpowiedział
 * jeszcze, o której faktycznie wyszedł. Po END_PROMPT_WINDOW_DAYS przestaje
 * dostawać modal, więc godzina kliknięcia (zwykle zawyżona — klika w domu)
 * wchodzi do raportu bez żadnego znaku zapytania. Ten badge jest jedynym
 * miejscem, w którym admin może to jeszcze wyłapać.
 *
 * Gdy pytanie zostało rozstrzygnięte, ślad jest już w historii zmian sesji —
 * drugiego sygnału tam nie dublujemy.
 */
function isRemoteEndPending(s: AdminWorkSession): boolean {
  return s.remoteEndPromptAt !== null && s.remoteEndResolvedAt === null
}

export default function AdminDay() {
  const { date } = useParams<{ date: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const userFilter = searchParams.get('user')
  const [filter, setFilter] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showOnlyOutside, setShowOnlyOutside] = useState(false)

  if (!date || !DATE_RE.test(date)) {
    return <Navigate to="/admin/calendar" replace />
  }

  const dateObj = parseISO(`${date}T00:00:00`)
  const prevDate = format(subDays(dateObj, 1), 'yyyy-MM-dd')
  const nextDate = format(addDays(dateObj, 1), 'yyyy-MM-dd')

  const { data, isLoading, error } = useAdminSessions({ from: date, to: date })

  const filteredUserMeta = useMemo(() => {
    if (!userFilter || !data) return null
    const fromSession = data.sessions.find((s) => s.userId === userFilter)?.user
    if (fromSession) return fromSession
    return null
  }, [userFilter, data])

  function clearUserFilter() {
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp)
        next.delete('user')
        return next
      },
      { replace: true },
    )
  }

  function navigateDay(targetDate: string) {
    const qs = userFilter ? `?user=${userFilter}` : ''
    navigate(`/admin/day/${targetDate}${qs}`)
  }

  const year = Number(date.slice(0, 4))
  const vacationsQuery = useAdminVacationRequests({
    status: 'APPROVED',
    year,
  })
  const vacationsToday = useMemo<AdminVacationRequest[]>(() => {
    const list = vacationsQuery.data?.requests ?? []
    return list.filter((r) => r.startDate <= date && r.endDate >= date)
  }, [vacationsQuery.data, date])

  const outsideCount = useMemo(
    () =>
      (data?.sessions ?? [])
        .filter((s) => (userFilter ? s.userId === userFilter : true))
        .filter((s) => s.outsideClinic).length,
    [data, userFilter],
  )

  const grouped = useMemo(() => {
    if (!data) return []
    const map = new Map<
      string,
      { user: AdminSessionUser; sessions: AdminWorkSession[] }
    >()
    for (const s of data.sessions) {
      if (!s.user) continue
      if (userFilter && s.userId !== userFilter) continue
      const entry = map.get(s.userId) ?? { user: s.user, sessions: [] }
      entry.sessions.push(s)
      map.set(s.userId, entry)
    }
    const list = Array.from(map.values())
    list.forEach((g) =>
      g.sessions.sort((a, b) => a.startedAt.localeCompare(b.startedAt)),
    )
    return list.sort((a, b) => a.user.name.localeCompare(b.user.name, 'pl'))
  }, [data, userFilter])

  const afterOutsideFilter = showOnlyOutside
    ? grouped
        .map((g) => ({
          ...g,
          sessions: g.sessions.filter((s) => s.outsideClinic),
        }))
        .filter((g) => g.sessions.length > 0)
    : grouped

  const filtered = filter
    ? afterOutsideFilter.filter((g) =>
        g.user.name.toLowerCase().includes(filter.toLowerCase()),
      )
    : afterOutsideFilter

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="secondary"
            onClick={() => navigateDay(prevDate)}
            aria-label="Poprzedni dzień"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">Wczoraj</span>
          </Button>
          <h1 className="text-xl sm:text-2xl font-semibold capitalize">
            {format(dateObj, 'EEEE, d MMMM yyyy', { locale: pl })}
          </h1>
          <Button
            variant="secondary"
            onClick={() => navigateDay(nextDate)}
            aria-label="Następny dzień"
          >
            <span className="hidden sm:inline">Jutro</span>
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/calendar"
            className="text-sm text-text-muted hover:text-text-primary"
          >
            ← Kalendarz
          </Link>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            Dodaj sesję
          </Button>
        </div>
      </div>

      {userFilter && (
        <div className="rounded-card border border-brand-accent/30 bg-brand-accent/5 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm">
            <span className="text-text-muted">Pokazuję sesje pracownika: </span>
            <span className="font-medium text-text-primary">
              {filteredUserMeta?.name ?? 'wybrany pracownik'}
            </span>
            {filteredUserMeta?.position && (
              <span className="text-text-muted">
                {' '}
                ({filteredUserMeta.position.name})
              </span>
            )}
          </div>
          <Button variant="ghost" onClick={clearUserFilter}>
            <X className="h-3.5 w-3.5" aria-hidden />
            Pokaż wszystkie
          </Button>
        </div>
      )}

      {(grouped.length > 5 || outsideCount > 0) && (
        <div className="flex flex-wrap items-center gap-4">
          {grouped.length > 5 && (
            <Input
              type="search"
              placeholder="Filtruj pracownika…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="max-w-xs"
            />
          )}
          {outsideCount > 0 && (
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showOnlyOutside}
                onChange={(e) => setShowOnlyOutside(e.target.checked)}
                className="rounded border-border-strong text-brand-accent focus:ring-brand-accent"
              />
              <span className="text-text-muted">
                Tylko poza placówką
                <span className="ml-1 text-text-faint">({outsideCount})</span>
              </span>
            </label>
          )}
        </div>
      )}

      {vacationsToday.length > 0 && (
        <section className="rounded-card border border-border-subtle bg-bg-elevated p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-text-faint mb-2">
            <Palmtree className="h-3.5 w-3.5" aria-hidden />
            Urlopy tego dnia
          </div>
          <ul className="space-y-1 text-sm">
            {vacationsToday.map((v) => (
              <li key={v.id}>
                <span className="font-medium text-text-primary">{v.user.name}</span>
                {v.user.position && (
                  <span className="text-text-muted"> ({v.user.position.name})</span>
                )}
                <span className="text-text-muted"> — {VACATION_TYPE_LABEL[v.type]}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {error && <Alert variant="error">{apiErrorMessage(error)}</Alert>}

      {isLoading && (
        <p className="text-sm text-text-muted inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Wczytywanie sesji…
        </p>
      )}

      {!isLoading && filtered.length === 0 && !error && (
        <div className="rounded-card border border-border-subtle bg-bg-elevated p-8 text-center text-text-muted">
          {showOnlyOutside ? (
            <>
              Brak sesji poza placówką
              {userFilter || filter ? ' dla wybranego pracownika' : ' tego dnia'}.
            </>
          ) : userFilter ? (
            <>
              Brak sesji tego dnia dla wybranego pracownika.
              {vacationsToday.some((v) => v.userId === userFilter) && (
                <div className="mt-2 text-sm">
                  Ten pracownik jest na urlopie w tym dniu — zobacz sekcję
                  „Urlopy tego dnia”.
                </div>
              )}
            </>
          ) : (
            <>
              Brak sesji tego dnia. Możesz{' '}
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                className="text-brand-accent hover:underline"
              >
                dodać sesję ręcznie
              </button>
              .
            </>
          )}
        </div>
      )}

      {filtered.map((group) => (
        <UserSessionCard
          key={group.user.id}
          user={group.user}
          sessions={group.sessions}
          editingId={editingId}
          onStartEdit={(id) => setEditingId(id)}
          onCancelEdit={() => setEditingId(null)}
        />
      ))}

      {showAddModal && (
        <AddSessionModal
          defaultDate={date}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
  )
}

interface UserSessionCardProps {
  user: AdminSessionUser
  sessions: AdminWorkSession[]
  editingId: string | null
  onStartEdit: (id: string) => void
  onCancelEdit: () => void
}

function UserSessionCard({
  user,
  sessions,
  editingId,
  onStartEdit,
  onCancelEdit,
}: UserSessionCardProps) {
  const totalSeconds = sessions.reduce((acc, s) => {
    const d = sessionDurationSec(s)
    return acc + (d ?? 0)
  }, 0)
  const hasAutoclosed = sessions.some((s) => s.autoClosed)
  const hasOpen = sessions.some((s) => !s.endedAt)

  return (
    <div className="rounded-card border border-border-subtle bg-bg-elevated overflow-hidden">
      <div className="px-4 py-3 bg-bg-surface flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-medium truncate">
            {user.name}
            {user.position && (
              <span className="text-text-muted font-normal">
                {' '}
                · {user.position.name}
              </span>
            )}
          </div>
          <div className="text-xs text-text-muted truncate">{user.email}</div>
        </div>
        <div className="text-sm text-text-muted flex items-center gap-3 flex-wrap">
          {hasAutoclosed && (
            <span className="inline-flex items-center gap-1 text-warning">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
              autozamknięta
            </span>
          )}
          {hasOpen && <span className="text-brand-accent">otwarta sesja</span>}
          <span>
            {sessions.length === 1
              ? '1 sesja'
              : sessions.length < 5
                ? `${sessions.length} sesje`
                : `${sessions.length} sesji`}
            {totalSeconds > 0 ? ` • ${formatDuration(totalSeconds)}` : ''}
          </span>
        </div>
      </div>
      <div>
        {sessions.map((s) =>
          editingId === s.id ? (
            <SessionRowEditing
              key={s.id}
              session={s}
              onCancelEdit={onCancelEdit}
            />
          ) : (
            <SessionRowCompact
              key={s.id}
              session={s}
              onStartEdit={() => onStartEdit(s.id)}
            />
          ),
        )}
      </div>
    </div>
  )
}

function Badge({
  children,
  tone = 'muted',
}: {
  children: React.ReactNode
  tone?: 'muted' | 'warning' | 'info'
}) {
  const styles =
    tone === 'warning'
      ? 'bg-warning/10 text-warning'
      : tone === 'info'
        ? 'bg-brand-accent/15 text-brand-accent'
        : 'bg-bg-surface text-text-muted'
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-pill px-2 py-0.5 text-[11px]',
        styles,
      )}
    >
      {children}
    </span>
  )
}

function SessionRowCompact({
  session,
  onStartEdit,
}: {
  session: AdminWorkSession
  onStartEdit: () => void
}) {
  const durationSec = sessionDurationSec(session)
  const startTime = format(parseISO(session.startedAt), 'HH:mm')
  const endTime = session.endedAt
    ? format(parseISO(session.endedAt), 'HH:mm')
    : null

  return (
    <button
      type="button"
      onClick={onStartEdit}
      className="w-full px-4 py-3 border-t border-border-subtle flex items-center justify-between gap-3 hover:bg-bg-surface/50 transition text-left"
    >
      <div className="flex items-center gap-3 flex-wrap min-w-0">
        <span className="font-mono text-sm whitespace-nowrap">
          {startTime} — {endTime ?? '(otwarta)'}
        </span>
        {durationSec !== null && (
          <span className="text-sm text-text-muted whitespace-nowrap">
            {formatDuration(durationSec)}
          </span>
        )}
        <div className="flex flex-wrap gap-1">
          {session.startedOutside && (
            <Badge tone="warning">
              <MapPinOff className="h-3 w-3 mr-1" aria-hidden />
              Start poza
              {session.startedDistanceM !== null && (
                <span className="ml-1 opacity-75">
                  (~{session.startedDistanceM} m)
                </span>
              )}
            </Badge>
          )}
          {session.endedOutside && (
            <Badge tone="warning">
              <MapPinOff className="h-3 w-3 mr-1" aria-hidden />
              Koniec poza
              {session.endedDistanceM !== null && (
                <span className="ml-1 opacity-75">
                  (~{session.endedDistanceM} m)
                </span>
              )}
            </Badge>
          )}
          {isRemoteEndPending(session) && (
            <Badge tone="warning">
              <Clock className="h-3 w-3 mr-1" aria-hidden />
              Czeka na godzinę wyjścia
              {session.remoteEndPromptDistanceM !== null && (
                <span className="ml-1 opacity-75">
                  ({formatDistancePl(session.remoteEndPromptDistanceM)})
                </span>
              )}
            </Badge>
          )}
          {session.autoClosed && (
            <Badge tone="warning">
              {session.autoCloseResolvedAt
                ? 'auto → korekta pracownika'
                : 'autozamknięta (czeka na pracownika)'}
            </Badge>
          )}
          {session.startedSource === 'ADMIN_ADDED' && (
            <Badge tone="info">dodana ręcznie</Badge>
          )}
          {session.endedSource === 'ADMIN_CORRECTION' && !session.autoClosed && (
            <Badge tone="info">korekta</Badge>
          )}
          {session.startedLat === null && <Badge>brak GPS</Badge>}
        </div>
      </div>
      <span className="text-xs text-brand-accent shrink-0">Edytuj</span>
    </button>
  )
}

const editSchema = z
  .object({
    startedAt: z.string().min(1, 'Podaj godzinę rozpoczęcia'),
    endedAt: z.string().optional().or(z.literal('')),
    notes: z.string().max(2000, 'Maksymalnie 2000 znaków').optional(),
  })
  .refine(
    (v) => {
      if (!v.endedAt) return true
      return new Date(v.endedAt).getTime() > new Date(v.startedAt).getTime()
    },
    { message: 'Koniec sesji musi być po jej rozpoczęciu.', path: ['endedAt'] },
  )

type EditValues = z.infer<typeof editSchema>

function SessionRowEditing({
  session,
  onCancelEdit,
}: {
  session: AdminWorkSession
  onCancelEdit: () => void
}) {
  const [showMap, setShowMap] = useState(false)
  const [showAudit, setShowAudit] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const update = useUpdateSession()
  const remove = useDeleteSession()

  const form = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      startedAt: toLocalInputFormat(session.startedAt),
      endedAt: session.endedAt ? toLocalInputFormat(session.endedAt) : '',
      notes: session.notes ?? '',
    },
  })

  async function onSubmit(values: EditValues) {
    const patch: {
      id: string
      startedAt?: string
      endedAt?: string | null
      notes?: string | null
    } = { id: session.id }
    const originalStart = toLocalInputFormat(session.startedAt)
    const originalEnd = session.endedAt
      ? toLocalInputFormat(session.endedAt)
      : ''
    const originalNotes = session.notes ?? ''

    if (values.startedAt !== originalStart) {
      patch.startedAt = fromLocalInputFormat(values.startedAt)
    }
    if (values.endedAt !== originalEnd) {
      patch.endedAt = values.endedAt ? fromLocalInputFormat(values.endedAt) : null
    }
    if ((values.notes ?? '') !== originalNotes) {
      patch.notes = values.notes ? values.notes : null
    }

    if (
      patch.startedAt === undefined &&
      patch.endedAt === undefined &&
      patch.notes === undefined
    ) {
      onCancelEdit()
      return
    }

    try {
      await update.mutateAsync(patch)
      toast.success('Sesja zaktualizowana')
      onCancelEdit()
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  async function onDelete() {
    try {
      await remove.mutateAsync(session.id)
      toast.success('Sesja usunięta')
      onCancelEdit()
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  return (
    <div className="px-4 py-3 border-t border-border-subtle bg-bg-surface/30">
      <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor={`start-${session.id}`}>Start</Label>
            <div className="flex items-center gap-2">
              <Input
                id={`start-${session.id}`}
                type="datetime-local"
                invalid={!!form.formState.errors.startedAt}
                {...form.register('startedAt')}
              />
              {session.startedAccuracyM !== null && (
                <span className="text-xs text-text-muted whitespace-nowrap inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3" aria-hidden />
                  ±{Math.round(session.startedAccuracyM)}m
                </span>
              )}
            </div>
            {form.formState.errors.startedAt && (
              <p className="mt-1 text-xs text-danger">
                {form.formState.errors.startedAt.message}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor={`end-${session.id}`}>Koniec (puste = otwarta)</Label>
            <div className="flex items-center gap-2">
              <Input
                id={`end-${session.id}`}
                type="datetime-local"
                invalid={!!form.formState.errors.endedAt}
                {...form.register('endedAt')}
              />
              {session.endedAccuracyM !== null && (
                <span className="text-xs text-text-muted whitespace-nowrap inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3" aria-hidden />
                  ±{Math.round(session.endedAccuracyM)}m
                </span>
              )}
            </div>
            {form.formState.errors.endedAt && (
              <p className="mt-1 text-xs text-danger">
                {form.formState.errors.endedAt.message}
              </p>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor={`notes-${session.id}`}>Notatka (opcjonalna)</Label>
          <textarea
            id={`notes-${session.id}`}
            rows={2}
            className="w-full rounded-card border border-border-subtle bg-bg-elevated px-3 py-2 text-sm placeholder:text-text-faint"
            {...form.register('notes')}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" loading={update.isPending}>
            Zapisz korektę
          </Button>
          <Button type="button" variant="secondary" onClick={onCancelEdit}>
            Anuluj
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setConfirmDelete(true)}
            className="text-danger hover:text-danger"
          >
            Usuń sesję
          </Button>
          {session.startedLat !== null && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowMap((v) => !v)}
            >
              <MapPin className="h-3.5 w-3.5" aria-hidden />
              {showMap ? 'Ukryj mapę' : 'Pokaż na mapie'}
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            onClick={() => setShowAudit((v) => !v)}
          >
            {showAudit ? (
              <ChevronUp className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" aria-hidden />
            )}
            Historia zmian
          </Button>
        </div>
      </form>

      {showMap && session.startedLat !== null && (
        <div className="mt-3">
          <Suspense
            fallback={
              <div className="h-64 rounded-card border border-border-subtle bg-bg-surface flex items-center justify-center text-sm text-text-muted">
                <Loader2 className="h-4 w-4 animate-spin mr-2" aria-hidden />
                Ładuję mapę…
              </div>
            }
          >
            <SessionMap session={session} />
          </Suspense>
        </div>
      )}

      {showAudit && (
        <div className="mt-3">
          <AuditTrail sessionId={session.id} />
        </div>
      )}

      {confirmDelete && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-bg-canvas/80 backdrop-blur-sm p-4"
        >
          <div className="rounded-card border border-border-subtle bg-bg-elevated p-6 max-w-md w-full">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle
                className="h-5 w-5 text-warning flex-shrink-0 mt-0.5"
                aria-hidden
              />
              <div>
                <h2 className="text-lg font-semibold mb-1">Usunąć sesję?</h2>
                <p className="text-sm text-text-muted">
                  Operacja jest nieodwracalna. Sesja zostanie usunięta z historii,
                  ale wpis w audit logu pozostanie.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setConfirmDelete(false)}
              >
                Anuluj
              </Button>
              <Button
                type="button"
                onClick={onDelete}
                loading={remove.isPending}
                className="bg-danger text-bg-elevated hover:opacity-90"
              >
                Tak, usuń
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
